import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle, AlertCircle, CreditCard, Banknote, Zap, Building2, Store, Check, Crown, Star, Users, Mail, CircleDot, Construction, Wrench } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { NavbarLight } from "../components/Navbar";
import { Footer } from "../components/Footer";
import SEO from "../components/SEO";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { BusinessFormFields } from "../components/listbusiness/BusinessFormFields";
import { PaymentSection } from "../components/listbusiness/PaymentSection";
import { usePiAuth } from "../contexts/PiAuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIER_ICONS = { free: Users, founding: Zap, professional: Star, featured: Crown };
const CORP_ICONS = { corporate_standard: Building2, corporate_professional: Star, corporate_premier: Crown };

const ListBusiness = () => {
  const [searchParams] = useSearchParams();
  const { createPiPayment, sdkReady: piSdkReady } = usePiAuth();
  const [categories, setCategories] = useState([]);
  const [towns, setTowns] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");

  const [smbTiers, setSmbTiers] = useState([]);
  const [corpTiers, setCorpTiers] = useState([]);
  const [industrialTiers, setIndustrialTiers] = useState([]);
  const [foundingSpotsLeft, setFoundingSpotsLeft] = useState(20);
  const [foundingCounts, setFoundingCounts] = useState({ business: { remaining: 20, max: 20 }, corporate: { remaining: 10, max: 10 }, industrial: { remaining: 2, max: 2 } });
  const [foundingConfig, setFoundingConfig] = useState({});
  const [corpAnnualDiscount, setCorpAnnualDiscount] = useState(10);
  const [taxRate, setTaxRate] = useState(0.05);
  const [taxLabel, setTaxLabel] = useState("GST");
  const [etransferEmail, setEtransferEmail] = useState("gold.manson@gmail.com");
  const [piConfig, setPiConfig] = useState({ enabled: false });

  const [activeTab, setActiveTab] = useState("business");
  const [selectedTier, setSelectedTier] = useState("free");
  const [corpBillingCycle, setCorpBillingCycle] = useState("annual");
  const [isFoundingSignup, setIsFoundingSignup] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("etransfer");

  const [formData, setFormData] = useState({
    name: "", owner_name: "", phone: "", email: "",
    website: "", address: "", town: "", category: "",
    description: "", hours: "",
  });

  useEffect(() => { fetchData(); checkPaymentStatus(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only

  const fetchData = async () => {
    try {
      const [catRes, tiersRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/membership/all-tiers`)
      ]);
      setCategories(catRes.data.categories);
      setTowns(catRes.data.towns);
      setSmbTiers(tiersRes.data.tiers || []);
      setCorpTiers(tiersRes.data.corporate_tiers || []);
      setIndustrialTiers(tiersRes.data.industrial_tiers || []);
      setFoundingSpotsLeft(tiersRes.data.founding_spots_remaining ?? 20);
      if (tiersRes.data.founding_counts) setFoundingCounts(tiersRes.data.founding_counts);
      if (tiersRes.data.founding_config) setFoundingConfig(tiersRes.data.founding_config);
      if (tiersRes.data.corporate_annual_discount) setCorpAnnualDiscount(tiersRes.data.corporate_annual_discount);
      if (tiersRes.data.tax) { setTaxRate(tiersRes.data.tax.rate); setTaxLabel(tiersRes.data.tax.label); }
      if (tiersRes.data.etransfer_email) setEtransferEmail(tiersRes.data.etransfer_email);
      if (tiersRes.data.pi) setPiConfig(tiersRes.data.pi);
    } catch (e) { console.error("Error fetching data:", e); }
  };

  const checkPaymentStatus = async () => {
    const sessionId = searchParams.get("session_id");
    const payment = searchParams.get("payment");
    if (payment === "cancelled") { setPaymentStatus("cancelled"); toast.error("Payment was cancelled."); return; }
    if (payment === "success" && sessionId) {
      setPaymentStatus("checking");
      let attempts = 0;
      const poll = async () => {
        attempts++;
        try {
          const res = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (res.data.payment_status === "paid") { setPaymentSuccess(true); setPaymentStatus("success"); toast.success("Payment successful!"); }
          else if (res.data.status === "expired") { setPaymentStatus("expired"); toast.error("Session expired."); }
          else if (attempts < 5) setTimeout(poll, 2000);
          else { setPaymentStatus("pending"); toast.info("Payment is being processed."); }
        } catch { if (attempts < 5) setTimeout(poll, 2000); else setPaymentStatus("error"); }
      };
      poll();
    }
  };

  const handleInputChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));
  const handleSelectChange = (name, value) => setFormData(p => ({ ...p, [name]: value }));

  const getFoundingCategory = () => {
    if (!isFoundingSignup) return null;
    if (activeTab === "corporate") return "corporate";
    if (activeTab === "industrial") return "industrial";
    return "business";
  };

  const getTabDescription = () => {
    if (activeTab === "corporate") return "Monthly or annual billing. Save 10% when you pay annually.";
    if (activeTab === "industrial") return "Enterprise-level regional partnership. Monthly or annual billing.";
    return "All plans are billed annually.";
  };

  const getPrice = () => {
    const isCorp = activeTab === "corporate";
    const isInd = activeTab === "industrial";
    const tiers = isInd ? industrialTiers : isCorp ? corpTiers : smbTiers;
    const tier = tiers.find(t => t.name === selectedTier);
    if (!tier) return { subtotal: 0, tax: 0, total: 0, label: "Free" };

    if (!isCorp && !isInd && tier.price_yearly === 0) return { subtotal: 0, tax: 0, total: 0, label: "Free" };

    let subtotal;
    if (isCorp || isInd) {
      subtotal = corpBillingCycle === "annual" ? tier.price_yearly : tier.price_monthly;
    } else {
      subtotal = tier.price_yearly;
    }

    // Apply founding discount
    let foundingSubtotal = subtotal;
    if (isFoundingSignup) {
      foundingSubtotal = Math.round(subtotal * 0.8 * 100) / 100;
    }

    const activeSubtotal = isFoundingSignup ? foundingSubtotal : subtotal;
    const tax = Math.round(activeSubtotal * taxRate * 100) / 100;
    const total = Math.round((activeSubtotal + tax) * 100) / 100;
    const period = ((isCorp || isInd) && corpBillingCycle === "monthly") ? "monthly" : "annual";

    return {
      subtotal: activeSubtotal,
      originalSubtotal: subtotal,
      tax, total,
      label: period === "monthly" ? `$${total}/mo` : `$${total.toLocaleString()}/yr`,
      period,
      annualSaved: (isCorp || isInd) && corpBillingCycle === "annual" ? Math.round((tier.price_monthly * 12 - tier.price_yearly) * 100) / 100 : 0,
      foundingDiscount: isFoundingSignup ? Math.round((subtotal - foundingSubtotal) * 100) / 100 : 0,
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.town || !formData.category || !formData.description) {
      toast.error("Please fill in all required fields"); return;
    }
    setSubmitting(true);
    try {
      const isCorp = activeTab === "corporate";
      const isInd = activeTab === "industrial";
      const isFree = !isCorp && !isInd && selectedTier === "free";
      const isPaid = !isFree;

      if (isPaid && paymentMethod === "stripe") {
        const res = await axios.post(`${API}/membership/subscribe`, {
          user_email: formData.email,
          user_name: formData.owner_name || formData.name,
          tier_name: selectedTier,
          billing_cycle: (isCorp || isInd) ? corpBillingCycle : "annual",
          origin_url: window.location.origin,
          is_founding: isFoundingSignup
        });
        if (res.data.url) {
          localStorage.setItem("pendingBusinessForm", JSON.stringify({
            ...formData,
            membership_tier: selectedTier,
            is_founding: isFoundingSignup,
            founding_category: getFoundingCategory(),
          }));
          window.location.href = res.data.url;
          return;
        }
      }

      if (isPaid && paymentMethod === "pi") {
        const priceData = getPriceInfo();
        const foundingCat = getFoundingCategory();
        try {
          await createPiPayment({
            amount: priceData.total,
            memo: `EK Valleys ${selectedTier} registration${isFoundingSignup ? " (Founding)" : ""}`,
            metadata: {
              product_type: "registration",
              tier_name: selectedTier,
              business_name: formData.name,
              email: formData.email,
              billing_cycle: (isCorp || isInd) ? corpBillingCycle : "annual",
              is_founding: isFoundingSignup,
              founding_category: foundingCat,
            },
          });
          // Pi payment completed — now submit the business listing
          await axios.post(`${API}/businesses`, {
            ...formData,
            membership_tier: selectedTier,
            is_founding: isFoundingSignup,
            founding_category: foundingCat,
          });
          toast.success("Payment successful! Your business has been submitted for review.");
          setFormData({ name: "", owner_name: "", phone: "", email: "", website: "", address: "", town: "", category: "", description: "", hours: "" });
          setSelectedTier("free");
          setSubmitting(false);
          return;
        } catch (err) {
          toast.error(err?.message || "Pi payment failed or was cancelled.");
          setSubmitting(false);
          return;
        }
      }
      await axios.post(`${API}/businesses`, {
        ...formData,
        membership_tier: selectedTier,
        is_founding: isFoundingSignup,
        founding_category: isFoundingSignup ? (activeTab === "corporate" ? "corporate" : activeTab === "industrial" ? "industrial" : "business") : null
      });
      toast.success("Your business has been submitted for review!");
      setFormData({ name: "", owner_name: "", phone: "", email: "", website: "", address: "", town: "", category: "", description: "", hours: "" });
      setSelectedTier("free");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit."); }
    setSubmitting(false);
  };

  useEffect(() => {
    if (paymentSuccess) {
      const pending = localStorage.getItem("pendingBusinessForm");
      if (pending) {
        (async () => {
          try {
            await axios.post(`${API}/businesses`, JSON.parse(pending));
            localStorage.removeItem("pendingBusinessForm");
            toast.success("Your business listing has been submitted!");
          } catch { toast.error("Issue submitting. Contact info@ekvalleys.com"); }
        })();
      }
    }
  }, [paymentSuccess]);

  // Reset selection when switching tabs
  useEffect(() => {
    if (activeTab === "business") setSelectedTier("free");
    else if (activeTab === "corporate" && corpTiers.length > 0) setSelectedTier(corpTiers[0].name);
    else if (activeTab === "industrial" && industrialTiers.length > 0) setSelectedTier(industrialTiers[0].name);
  }, [activeTab, corpTiers, industrialTiers]);

  const priceInfo = getPrice();
  const isCorp = activeTab === "corporate";
  const isInd = activeTab === "industrial";
  const activeTiers = isInd ? industrialTiers : isCorp ? corpTiers : smbTiers;
  const currentTier = activeTiers.find(t => t.name === selectedTier);
  const showPayment = isCorp || isInd || (selectedTier !== "free");

  return (
    <div className="min-h-screen bg-[#EFEDE5]">
      <SEO title="List Your Business" description="Get your business listed in East Kootenay's community directory." />
      <NavbarLight />

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-10">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#1A1915] mb-3">List Your Business</h1>
          <p className="text-[#5C5A52] text-lg">Join East Kootenay's digital home.</p>
        </div>

        {/* Development Notice */}
        <div className="bg-[#D4A017]/10 border-2 border-[#D4A017]/40 rounded-xl p-5 mb-8 flex items-start gap-3" data-testid="dev-notice-banner">
          <Construction className="w-6 h-6 text-[#D4A017] mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="font-heading text-base font-semibold text-[#1A1915]">Early Development & Launch Phase</h3>
            <p className="text-[#5C5A52] text-sm">
              EkValleys is currently in the early development and launch phase. While final registration and banking are being completed, initial launch partnerships are being coordinated directly via <strong>E-Transfer</strong>.
            </p>
          </div>
        </div>

        {/* Payment Banners */}
        {paymentStatus === "success" && (
          <div className="bg-[#14B8A6]/10 border-2 border-[#14B8A6] rounded-xl p-6 mb-8 flex items-start gap-3">
            <CheckCircle className="w-6 h-6 text-[#14B8A6] mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#1A1915]">Payment Confirmed!</h3>
              <p className="text-[#5C5A52]">Your membership is active. Your listing will be reviewed shortly.</p>
            </div>
          </div>
        )}
        {paymentStatus === "cancelled" && (
          <div className="bg-red-50 border-2 border-red-300 rounded-xl p-6 mb-8 flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-red-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-heading text-lg font-semibold text-[#1A1915]">Payment Cancelled</h3>
              <p className="text-[#5C5A52]">No worries — you can still submit or try again.</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Business Details */}
          <BusinessFormFields
            formData={formData}
            handleInputChange={handleInputChange}
            handleSelectChange={handleSelectChange}
            towns={towns}
            categories={categories}
          />

          {/* Plan Selection */}
          <div className="bg-[#F7F6F2] border-2 border-[#D1CFC5] rounded-xl p-6 md:p-8">
            <h2 className="font-heading text-2xl font-semibold text-[#1A1915] mb-2">Choose Your Plan</h2>
            <p className="text-[#5C5A52] text-sm mb-6">{getTabDescription()}</p>

            {/* Business / Corporate / Industrial Toggle */}
            <div className="flex justify-center mb-6">
              <div className="bg-[#D1CFC5] p-1 rounded-lg inline-flex">
                <button type="button" onClick={() => { setActiveTab("business"); setSelectedTier("free"); setIsFoundingSignup(false); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "business" ? "bg-white text-[#1A1915] shadow-sm" : "text-[#5C5A52]"}`}
                  data-testid="tab-business">
                  <Store className="w-4 h-4" /> Business
                </button>
                <button type="button" onClick={() => { setActiveTab("corporate"); setSelectedTier("corporate_standard"); setIsFoundingSignup(false); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "corporate" ? "bg-white text-[#1A1915] shadow-sm" : "text-[#5C5A52]"}`}
                  data-testid="tab-corporate">
                  <Building2 className="w-4 h-4" /> Corporate
                </button>
                <button type="button" onClick={() => { setActiveTab("industrial"); setSelectedTier("industrial_standard"); setIsFoundingSignup(false); }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === "industrial" ? "bg-white text-[#1A1915] shadow-sm" : "text-[#5C5A52]"}`}
                  data-testid="tab-industrial">
                  <Wrench className="w-4 h-4" /> Industrial
                </button>
              </div>
            </div>

            {/* Founding Signup Toggle */}
            {(() => {
              const cat = getFoundingCategory() || "business";
              const fc = foundingCounts[cat] || {};
              const spotsLeft = fc.remaining ?? 0;
              const maxSpots = fc.max ?? 0;
              if (activeTab === "business" && selectedTier === "free") return null;
              return (
                <div className={`mb-6 border-2 rounded-xl p-4 transition-all ${isFoundingSignup ? "border-[#14B8A6] bg-[#14B8A6]/5" : "border-[#D1CFC5] bg-white"}`} data-testid="founding-toggle-section">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Zap className="w-5 h-5 text-[#14B8A6]" />
                      <div>
                        <p className="font-semibold text-[#1A1915] text-sm">
                          {cat === "business" ? "Founding Business Member" : cat === "corporate" ? "Founding Corporate Partner" : "Founding Industrial Partner"}
                        </p>
                        <p className="text-xs text-[#5C5A52]">Locked in at 20% off yearly registration price for life — Guaranteed.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${spotsLeft > 0 ? "bg-[#14B8A6]/20 text-[#14B8A6]" : "bg-red-100 text-red-600"}`}>
                        {spotsLeft > 0 ? `${spotsLeft} of ${maxSpots} spots` : "SOLD OUT"}
                      </span>
                      <button type="button" disabled={spotsLeft <= 0}
                        onClick={() => setIsFoundingSignup(!isFoundingSignup)}
                        className={`w-12 h-6 rounded-full transition-colors relative ${isFoundingSignup ? "bg-[#14B8A6]" : "bg-[#D1CFC5]"} ${spotsLeft <= 0 ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        data-testid="founding-toggle">
                        <div className={`w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${isFoundingSignup ? "translate-x-6" : "translate-x-0.5"}`} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* SMB Tiers */}
            {!isCorp && !isInd && (
              <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {smbTiers.map((tier) => {
                  const Icon = TIER_ICONS[tier.name] || Users;
                  const isSelected = selectedTier === tier.name;
                  const isGradient = tier.visual_upgrade_style === "gradient";
                  const isFounding = tier.name === "founding";
                  const isFoundingSoldOut = isFounding && foundingSpotsLeft <= 0;
                  const showFoundingPrice = isFoundingSignup && tier.price_yearly > 0;
                  const foundingPrice = showFoundingPrice ? Math.round(tier.price_yearly * 0.8 * 100) / 100 : null;

                  return (
                    <div key={tier.name}
                      onClick={() => !isFoundingSoldOut && setSelectedTier(tier.name)}
                      className={`relative p-4 rounded-xl border-2 transition-all ${isFoundingSoldOut ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${
                        isSelected ? "border-[#2C4A3B]" : "border-[#D1CFC5] hover:border-[#5C5A52]"
                      } ${isGradient ? "bg-gradient-to-br from-[#1A2F23] to-[#2C4A3B] text-white" : isFounding ? "bg-[#14B8A6]/5" : "bg-[#F7F6F2]"}`}
                      style={isSelected && !isGradient ? { borderColor: isFounding ? "#14B8A6" : "#2C4A3B" } : {}}
                      data-testid={`tier-${tier.name}`}>

                      {isFounding && foundingSpotsLeft > 0 && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#14B8A6] text-white whitespace-nowrap">
                          {foundingSpotsLeft} SPOTS LEFT
                        </div>
                      )}
                      {tier.visual_upgrade && !isFounding && (
                        <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                          style={{ backgroundColor: tier.badge_colors?.secondary, color: tier.badge_colors?.primary }}>
                          {tier.name === "featured" ? "BEST VALUE" : "POPULAR"}
                        </div>
                      )}

                      <div className="text-center mb-3">
                        <Icon className="w-7 h-7 mx-auto mb-1" style={{ color: isGradient ? "#D4A017" : isFounding ? "#14B8A6" : "#2C4A3B" }} />
                        <h3 className={`font-heading font-bold ${isGradient ? "text-white" : "text-[#1A1915]"}`}>{tier.display_name}</h3>
                      </div>
                      <div className="text-center mb-3">
                        {showFoundingPrice ? (
                          <>
                            <p className={`text-sm line-through ${isGradient ? "text-white/40" : "text-[#5C5A52]"}`}>${tier.price_yearly}/yr</p>
                            <p className={`text-2xl font-bold text-[#14B8A6]`}>${foundingPrice}<span className="text-xs font-normal">/yr</span></p>
                          </>
                        ) : (
                          <>
                            <p className={`text-2xl font-bold ${isGradient ? "text-white" : "text-[#1A1915]"}`}>
                              {tier.price_yearly === 0 ? "Free" : `$${tier.price_yearly}`}
                            </p>
                            <p className={`text-xs ${isGradient ? "text-white/60" : "text-[#5C5A52]"}`}>
                              {tier.price_yearly === 0 ? "Basic listing" : "/year"}
                            </p>
                          </>
                        )}
                      </div>
                      <ul className="space-y-1.5 text-xs">
                        {tier.features.map((f, i) => (
                          <li key={`feat-${i}-${f.slice(0,10)}`} className={`flex items-start gap-1.5 ${isGradient ? "text-white/80" : "text-[#5C5A52]"}`}>
                            <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: isFounding ? "#14B8A6" : "#2C4A3B" }} />{f}
                          </li>
                        ))}
                      </ul>
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: isFounding ? "#14B8A6" : "#2C4A3B" }}>
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </>
            )}

            {/* Corporate Tiers */}
            {isCorp && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="bg-[#1E3A5F]/10 p-1 rounded-lg inline-flex">
                    <button type="button" onClick={() => setCorpBillingCycle("monthly")}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${corpBillingCycle === "monthly" ? "bg-white text-[#1A1915] shadow-sm" : "text-[#5C5A52]"}`}
                      data-testid="corp-monthly">Monthly</button>
                    <button type="button" onClick={() => setCorpBillingCycle("annual")}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${corpBillingCycle === "annual" ? "bg-white text-[#1A1915] shadow-sm" : "text-[#5C5A52]"}`}
                      data-testid="corp-annual">Annual <span className="text-[#14B8A6] text-xs font-bold">Save {corpAnnualDiscount}%</span></button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  {corpTiers.map((tier) => {
                    const Icon = CORP_ICONS[tier.name] || Building2;
                    const isSelected = selectedTier === tier.name;
                    const isGradient = tier.visual_upgrade_style === "gradient";
                    const price = corpBillingCycle === "annual" ? tier.price_yearly : tier.price_monthly;
                    const foundingPrice = isFoundingSignup ? Math.round(price * 0.8 * 100) / 100 : null;
                    return (
                      <div key={tier.name} onClick={() => setSelectedTier(tier.name)}
                        className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? "border-[#1E3A5F]" : "border-[#D1CFC5] hover:border-[#5C5A52]"} ${isGradient ? "bg-gradient-to-br from-[#1E3A5F] to-[#2C4A7B] text-white" : "bg-[#F7F6F2]"}`}
                        data-testid={`tier-${tier.name}`}>
                        {tier.visual_upgrade && (
                          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                            style={{ backgroundColor: tier.badge_colors?.secondary, color: tier.badge_colors?.primary }}>
                            {tier.name === "corporate_premier" ? "BEST VALUE" : "POPULAR"}
                          </div>
                        )}
                        <div className="text-center mb-3">
                          <Icon className="w-8 h-8 mx-auto mb-1" style={{ color: isGradient ? "#D4A017" : "#1E3A5F" }} />
                          <h3 className={`font-heading font-bold ${isGradient ? "text-white" : "text-[#1A1915]"}`}>{tier.display_name}</h3>
                        </div>
                        <div className="text-center mb-3">
                          {isFoundingSignup ? (
                            <>
                              <p className={`text-sm line-through ${isGradient ? "text-white/40" : "text-[#5C5A52]"}`}>${price.toLocaleString()}</p>
                              <p className="text-2xl font-bold text-[#14B8A6]">${foundingPrice.toLocaleString()}<span className="text-xs font-normal">/{corpBillingCycle === "annual" ? "yr" : "mo"}</span></p>
                            </>
                          ) : (
                            <>
                              <p className={`text-2xl font-bold ${isGradient ? "text-white" : "text-[#1A1915]"}`}>${price.toLocaleString()}</p>
                              <p className={`text-xs ${isGradient ? "text-white/60" : "text-[#5C5A52]"}`}>{corpBillingCycle === "annual" ? "/year" : "/month"}</p>
                            </>
                          )}
                        </div>
                        {corpBillingCycle === "annual" && !isFoundingSignup && (
                          <p className="text-center text-xs text-[#14B8A6] font-medium mb-3">Save ${Math.round(tier.price_monthly * 12 - tier.price_yearly)}/yr</p>
                        )}
                        <ul className="space-y-1.5 text-xs">
                          {tier.features.map((f, i) => (
                            <li key={`feat-${i}-${f.slice(0,10)}`} className={`flex items-start gap-1.5 ${isGradient ? "text-white/80" : "text-[#5C5A52]"}`}>
                              <Check className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#1E3A5F]" />{f}
                            </li>
                          ))}
                        </ul>
                        {isSelected && (<div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#1E3A5F] flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>)}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Industrial Tiers */}
            {isInd && (
              <>
                <div className="flex justify-center mb-4">
                  <div className="bg-[#7C3AED]/10 p-1 rounded-lg inline-flex">
                    <button type="button" onClick={() => setCorpBillingCycle("monthly")}
                      className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${corpBillingCycle === "monthly" ? "bg-white text-[#1A1915] shadow-sm" : "text-[#5C5A52]"}`}
                      data-testid="ind-monthly">Monthly</button>
                    <button type="button" onClick={() => setCorpBillingCycle("annual")}
                      className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${corpBillingCycle === "annual" ? "bg-white text-[#1A1915] shadow-sm" : "text-[#5C5A52]"}`}
                      data-testid="ind-annual">Annual <span className="text-[#14B8A6] text-xs font-bold">Save 10%</span></button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                  {industrialTiers.map((tier) => {
                    const isSelected = selectedTier === tier.name;
                    const isGradient = tier.visual_upgrade_style === "gradient";
                    const price = corpBillingCycle === "annual" ? tier.price_yearly : tier.price_monthly;
                    const foundingPrice = isFoundingSignup ? Math.round(price * 0.8 * 100) / 100 : null;
                    return (
                      <div key={tier.name} onClick={() => setSelectedTier(tier.name)}
                        className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? "border-[#7C3AED]" : "border-[#D1CFC5] hover:border-[#5C5A52]"} ${isGradient ? "bg-gradient-to-br from-[#4C1D95] to-[#7C3AED] text-white" : "bg-[#F7F6F2]"}`}
                        data-testid={`tier-${tier.name}`}>
                        {tier.visual_upgrade && (
                          <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap bg-[#D4A017] text-[#1A1915]">
                            {tier.name === "industrial_premier" ? "BEST VALUE" : "POPULAR"}
                          </div>
                        )}
                        <div className="text-center mb-3">
                          <Wrench className="w-8 h-8 mx-auto mb-1" style={{ color: isGradient ? "#D4A017" : "#7C3AED" }} />
                          <h3 className={`font-heading font-bold ${isGradient ? "text-white" : "text-[#1A1915]"}`}>{tier.display_name}</h3>
                        </div>
                        <div className="text-center mb-3">
                          {isFoundingSignup ? (
                            <>
                              <p className={`text-sm line-through ${isGradient ? "text-white/40" : "text-[#5C5A52]"}`}>${price.toLocaleString()}</p>
                              <p className="text-2xl font-bold text-[#14B8A6]">${foundingPrice.toLocaleString()}<span className="text-xs font-normal">/{corpBillingCycle === "annual" ? "yr" : "mo"}</span></p>
                            </>
                          ) : (
                            <>
                              <p className={`text-2xl font-bold ${isGradient ? "text-white" : "text-[#1A1915]"}`}>${price.toLocaleString()}</p>
                              <p className={`text-xs ${isGradient ? "text-white/60" : "text-[#5C5A52]"}`}>{corpBillingCycle === "annual" ? "/year" : "/month"}</p>
                            </>
                          )}
                        </div>
                        {corpBillingCycle === "annual" && !isFoundingSignup && (
                          <p className="text-center text-xs text-[#14B8A6] font-medium mb-3">Save ${Math.round(tier.price_monthly * 12 - tier.price_yearly)}/yr</p>
                        )}
                        <ul className="space-y-1.5 text-xs">
                          {tier.features.map((f, i) => (
                            <li key={`feat-${i}-${f.slice(0,10)}`} className={`flex items-start gap-1.5 ${isGradient ? "text-white/80" : "text-[#5C5A52]"}`}>
                              <Check className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#7C3AED]" />{f}
                            </li>
                          ))}
                        </ul>
                        {isSelected && (<div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[#7C3AED] flex items-center justify-center"><Check className="w-3 h-3 text-white" /></div>)}
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* Price Summary + Payment (paid tiers) */}
            {showPayment && (selectedTier !== "free") && (
              <PaymentSection
                isInd={isInd} isCorp={isCorp} priceInfo={priceInfo}
                corpBillingCycle={corpBillingCycle} corpAnnualDiscount={corpAnnualDiscount}
                isFoundingSignup={isFoundingSignup} taxRate={taxRate} taxLabel={taxLabel}
                currentTier={currentTier} paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod} etransferEmail={etransferEmail}
                piConfig={piConfig}
              />
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-center">
            <Button type="submit" disabled={submitting}
              className={`${isInd ? "bg-[#4C1D95] hover:bg-[#3B1578]" : isCorp ? "bg-[#1E3A5F] hover:bg-[#152B47]" : "bg-[#2C4A3B] hover:bg-[#1A2F23]"} text-white font-semibold text-lg px-12 py-6 rounded-xl`}
              data-testid="submit-listing-btn">
              {submitting ? "Submitting..." : selectedTier === "free" ? "Submit Listing" : paymentMethod === "pi" ? "Pay with Pi & Submit" : paymentMethod === "stripe" ? "Continue to Payment" : "Submit Listing (E-Transfer)"}
            </Button>
          </div>
        </form>
      </div>

      <Footer />
    </div>
  );
};

export default ListBusiness;
